import { useState, useRef, useEffect } from 'react'
import { askAI, generateQuiz } from '../services/api'
import { useAuthStore } from '../hooks/useAuth'
import {
  Bot, Send, User, Sparkles, BookOpen, FileText, Lightbulb,
  Loader2, Copy, CheckCircle, Trash2, Download, Wand2
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  type?: 'text' | 'quiz' | 'explanation'
}

export default function AIAssistant() {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: `Hello ${user?.name || 'there'}! I'm your AI learning assistant powered by GPT-4. I can help you with:

• Answering questions about any subject
• Generating practice quizzes
• Explaining complex topics
• Grading your essays
• Creating study plans
• Summarizing course materials

What would you like to learn today?`,
      timestamp: new Date(),
      type: 'text'
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMode, setSelectedMode] = useState<'chat' | 'quiz' | 'grade'>('chat')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
      // In production: const response = await askAI(input, context)
      // Simulated AI response for demo
      const aiResponse = await simulateAIResponse(input, selectedMode)

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date(),
        type: selectedMode === 'quiz' ? 'quiz' : 'text'
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch {
      toast.error('AI service unavailable. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const simulateAIResponse = async (question: string, mode: string): Promise<string> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    if (mode === 'quiz') {
      return `🎯 **Generated Quiz: ${question}**

**Question 1:** What is the primary function of mitochondria?
A) Protein synthesis
B) Energy production
C) Cell division
D) Waste removal

**Question 2:** Solve: 2x + 5 = 13
A) x = 3
B) x = 4
C) x = 5
D) x = 6

**Question 3:** Which gas do plants absorb from the atmosphere?
A) Oxygen
B) Carbon Dioxide
C) Nitrogen
D) Hydrogen

✅ Answers: 1-B, 2-B, 3-B`
    }

    if (question.toLowerCase().includes('math') || question.toLowerCase().includes('calculate')) {
      return `📐 **Mathematical Solution**

Let me break this down step by step:

1. **Identify the problem**: ${question}
2. **Apply the formula**: Use appropriate mathematical principles
3. **Calculate**: Show all working steps
4. **Verify**: Double-check the result

**Answer**: The solution requires applying [specific method based on your question]. Would you like me to explain a specific concept in more detail?`
    }

    if (question.toLowerCase().includes('explain') || question.toLowerCase().includes('what is')) {
      return `💡 **Explanation**

${question} is a fascinating topic! Here's a comprehensive breakdown:

**Key Concepts:**
• Definition and fundamentals
• Real-world applications
• Common misconceptions
• Advanced considerations

**Study Tips:**
1. Break complex ideas into smaller parts
2. Use visual aids and diagrams
3. Practice with examples
4. Teach someone else to reinforce learning

Would you like me to generate practice questions on this topic?`
    }

    return `🤖 **AI Response**

Great question! Based on my analysis:

${question}

**Key Points:**
• This topic connects to several important concepts
• Practical applications include...
• Common challenges students face...

**Recommended Next Steps:**
1. Review related course materials
2. Try the practice quiz I can generate
3. Ask follow-up questions for deeper understanding

Is there a specific aspect you'd like me to elaborate on?`
  }

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
    toast.success('Copied to clipboard!')
  }

  const clearChat = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: 'Chat cleared. How can I help you today?',
      timestamp: new Date(),
      type: 'text'
    }])
  }

  return (
    <div className="space-y-6 animate-fade-in h-[calc(100vh-6rem)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" />
            AI Learning Assistant
          </h1>
          <p className="text-slate-500 mt-1">Powered by GPT-4 • Ask anything about your studies</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearChat}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="flex gap-2">
        {[
          { id: 'chat', label: 'Chat', icon: Bot },
          { id: 'quiz', label: 'Generate Quiz', icon: FileText },
          { id: 'grade', label: 'Grade Essay', icon: CheckCircle },
        ].map(mode => {
          const Icon = mode.icon
          return (
            <button
              key={mode.id}
              onClick={() => setSelectedMode(mode.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedMode === mode.id
                  ? 'bg-primary text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {mode.label}
            </button>
          )
        })}
      </div>

      {/* Chat Area */}
      <div className="glass rounded-xl flex flex-col h-[calc(100%-8rem)]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                msg.role === 'assistant' ? 'bg-primary/20' : 'bg-slate-700'
              }`}>
                {msg.role === 'assistant' ? (
                  <Bot className="w-4 h-4 text-primary" />
                ) : (
                  <User className="w-4 h-4 text-slate-400" />
                )}
              </div>
              <div className={`max-w-[80%] rounded-xl p-3 ${
                msg.role === 'assistant'
                  ? 'bg-slate-800/50 border border-slate-700'
                  : 'bg-primary/20 border border-primary/30'
              }`}>
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-600">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => copyMessage(msg.content)}
                      className="p-1 hover:bg-slate-700 rounded transition-colors"
                    >
                      <Copy className="w-3 h-3 text-slate-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary" />
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  AI is thinking...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder={
                  selectedMode === 'quiz' ? 'Enter a topic to generate a quiz...' :
                  selectedMode === 'grade' ? 'Paste your essay here for grading...' :
                  'Ask me anything about your studies...'
                }
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary resize-none min-h-[60px] max-h-[120px]"
                rows={1}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="p-3 bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-slate-600 mt-2">
            <Sparkles className="w-3 h-3 inline mr-1" />
            AI can make mistakes. Verify important information.
          </p>
        </div>
      </div>
    </div>
  )
}
